import { Permission } from '../../api/roles.dao';
import { CONNECTION_TYPE, FormInputType } from '../../components/_events/_manage-event/shared/connections-list/_connection-form/connection-form.viewmodel';
import { hasPermission } from '../../infrastructure/utils/has-permissions';
import { ConnectionFields as Fields } from './config';
import { ConnectionType } from '../../api/connections.dao';
import { ConnectionVersion } from '../../constants/common.constants';
import { webinarSyncMeetingAttendeeInfoMessageV2, zoomMeetingAutoSyncInfoMessageV2 } from '../../constants/connections-labels.constants';

/* Add ConnectionType read-only form field  */
const appendConnectionType = (connectionType: string) =>
    (target: object, propertyKey: string, descriptor: PropertyDescriptor) => {
        const originalMethod = descriptor.value;

        descriptor.value = function _appendConnectionType() {
            const connectionTypeField = {
                fieldType: FormInputType.TextInput, readOnly: true, label: 'Connection Type', name: Fields.TYPE,
                value: Reflect.get(this, connectionType) || ''
            };

            return [ connectionTypeField, ...originalMethod.apply(this) ];
        };

        return descriptor;
    };

/* Show/Hide renderAdditionalKnovioConnectionFields appropriately to the choosen enabled/disabled fields for Knovio Connection Type */
const renderAdditionalKnovioConnectionFields = (useDecorator: string) =>
    (target: object, propertyKey: string, descriptor: PropertyDescriptor) => {
        const originalMethod = descriptor.value;
        const liveStreamFieldNames = [
            Fields.PRIMARY_LIVE_STREAM_NAME, Fields.PRIMARY_LIVE_STREAM_URL, Fields.BACKUP_LIVE_STREAM_NAME, Fields.BACKUP_LIVE_STREAM_URL
        ];

        descriptor.value = function _renderLiveStreamFields() {
            let fields = originalMethod.apply(this);

            if (Reflect.get(this, useDecorator)) {
                if (!Reflect.get(this, 'shouldShowKnovioReplayToggle')) {
                    fields = fields.filter(({ name }) => name !== Fields.PASSPORT_REPLAY);
                }

                const { value: publishConnection = false } = fields.find(({name}) => name === Fields.PUBLISH_CONNECTION) || {};
                if (publishConnection) {
                    fields = fields.filter(({ name }) => name !== Fields.DETAILS_PLACEHOLDER_TEXT);
                }

                const { value = false } = fields.find(({name}) => name === Fields.LIVE_STREAM_ENABLED) || {};

                return value ? fields : fields.filter(({ name }) => !liveStreamFieldNames.includes(name));
            }
            return fields;
        };

        return descriptor;
    };
const renderAdditionalOtherConnectionFields = (useDecorator: string) =>
    (target: object, propertyKey: string, descriptor: PropertyDescriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function _renderLiveStreamFieldsForOther() {
            let fields = originalMethod.apply(this);
            if (Reflect.get(this, useDecorator)) {
                if (!Reflect.get(this, 'shouldShowKnovioReplayToggle')) {
                    fields = fields.filter(({ name }) => name !== Fields.PASSPORT_REPLAY);
                }
            }
            return fields;
        };

        return descriptor;
    };

/* Show/Hide ZoomMeeting/Webinar Fields appropriately to the existing or not Connection ID for ZoomMeeting/Webinar Connection Type */
const renderZoomFields = (useDecorator: string, connectionVersionKey: string) =>
    (target: object, propertyKey: string, descriptor: PropertyDescriptor) => {
        const originalMethod = descriptor.value;
        const webinarFieldsNames = [
            Fields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED,
            Fields.AUTO_REGISTRATION_ENABLED,
            Fields.REGISTRATION_ENABLED,
            Fields.AUTOMATICALLY_APPROVAL,
            Fields.REGISTRATION_CONFIRMATION_EMAIL,
            Fields.AUTOMATICALLY_RECORD
        ];


        descriptor.value = function _renderWebinarFields() {
            let fields = originalMethod.apply(this);

            /* [OEP-11477] we should not render Fields.AUTO_REGISTRATION_ENABLED for V2 connection */
            if (Reflect.get(this, connectionVersionKey) === ConnectionVersion.V2) {
                fields = fields.filter(({ name }) => name !== Fields.AUTO_REGISTRATION_ENABLED);

                /* show specific info messages for V2 SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED field */
                const syncMeetingFieldIndex = fields.findIndex(({ name }) => name === Fields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED);

                if (syncMeetingFieldIndex !== -1) {
                    fields[syncMeetingFieldIndex].toggleInfoMessage = {
                        [ConnectionType.Webinar]: webinarSyncMeetingAttendeeInfoMessageV2,
                        [ConnectionType.Zoom]: zoomMeetingAutoSyncInfoMessageV2,
                    };
                }
            }

            /* [OEP-7473] we render Fields.AUTOMATICALLY_RECORD only for the Webinar */
            if (Reflect.get(this, CONNECTION_TYPE) !== ConnectionType.Webinar) {
                fields = fields.filter(({ name }) => name !== Fields.AUTOMATICALLY_RECORD);
            }

            if (
                   !Reflect.get(this, 'isMeetingConnectionsForm')
                || !Reflect.get(this, useDecorator)
                || !Reflect.get(this, 'showZoomAutoRegAndAutoSyncToggles')
            ) {
                return fields.filter(({ name }) => !webinarFieldsNames.includes(name));
            }

            if (!Reflect.get(this, 'showZoomAutoRegAndAutoSyncToggles')) {
                fields = fields.filter(({ name }) => name !== Fields.AUTO_REGISTRATION_ENABLED);
            }

            if (!hasPermission(Permission.CREATE_ZOOM_CONNECTIONS)) {
                fields = fields.filter(({ name }) => name !== Fields.REGISTRATION_CONFIRMATION_EMAIL);
            }

            const { value = true } = fields.find(({name}) => name === Fields.REGISTRATION_ENABLED) || {};
            return value ? fields : fields.filter(({ name }) => name !== Fields.AUTOMATICALLY_APPROVAL
                && name !== Fields.REGISTRATION_CONFIRMATION_EMAIL);

        };

        return descriptor;
    };

export {
    appendConnectionType,
    renderAdditionalKnovioConnectionFields,
    renderAdditionalOtherConnectionFields,
    renderZoomFields,
};
